<template>
  <v-container fluid class="pa-4">
    <!-- Page Header -->
    <v-row class="mb-6">
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-avatar color="primary" size="48" class="mr-4">
              <v-icon color="white" size="24">mdi-shield-account</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-bold text--primary mb-1">
                {{ $t('role.title.role') }}
              </h1>
              <p class="text--secondary text-subtitle-1 mb-0">
                {{ $t('role.dashboard.manage_all_roles') }}
              </p>
            </div>
          </div>
          <v-btn
            color="primary"
            large
            elevation="2"
            :to="{ name: 'create-role' }"
            v-can="'create roles'"
          >
            <v-icon left>mdi-plus</v-icon>
            {{ $t('role.button.create') }}
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Statistics Cards -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card color="primary" dark class="elevation-4">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h3 font-weight-bold">{{ roles.length }}</div>
                <div class="text-subtitle-1 opacity-90">{{ $t('role.dashboard.total_roles') }}</div>
              </div>
              <v-avatar color="primary" size="48">
                <v-icon size="24">mdi-shield-account</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card color="success" dark class="elevation-4">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h3 font-weight-bold">{{ activeRoles }}</div>
                <div class="text-subtitle-1 opacity-90">{{ $t('role.dashboard.active_roles') }}</div>
              </div>
              <v-avatar color="success" size="48">
                <v-icon size="24">mdi-shield-check</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card color="info" dark class="elevation-4">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h3 font-weight-bold">{{ systemRoles }}</div>
                <div class="text-subtitle-1 opacity-90">{{ $t('role.dashboard.system_roles') }}</div>
              </div>
              <v-avatar color="info" size="48">
                <v-icon size="24">mdi-shield-crown</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card color="warning" dark class="elevation-4">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h3 font-weight-bold">{{ customRoles }}</div>
                <div class="text-subtitle-1 opacity-90">{{ $t('role.dashboard.custom_roles') }}</div>
              </div>
              <v-avatar color="warning" size="48">
                <v-icon size="24">mdi-shield-edit</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Data Table -->
    <v-card class="elevation-2">
      <v-card-title class="pa-4">
        <v-icon color="primary" class="mr-2">mdi-table</v-icon>
        {{ $t('role.button.all') }}
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          :label="$t('text.search_placeholder')"
          single-line
          hide-details
          clearable
          class="search-field"
        ></v-text-field>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="filteredRoles"
        :search="search"
        :loading="loading"
        :items-per-page="15"
        class="elevation-0"
        :footer-props="{
          'items-per-page-options': [10, 15, 25, 50, 100],
          'items-per-page-text': $t('table.items_per_page')
        }"
      >
        <!-- Role Name Column -->
        <template slot="item.name" slot-scope="{ item }">
          <div class="d-flex align-center">
            <v-avatar :color="getRoleColor(item.name)" size="32" class="mr-3">
              <v-icon color="white" size="16">mdi-shield</v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-medium">{{ item.name }}</div>
              <div v-if="item.system" class="text-caption text--secondary">
                <v-chip x-small color="primary" outlined>{{ $t('role.label.system') }}</v-chip>
              </div>
            </div>
          </div>
        </template>

        <!-- Description Column -->
        <template slot="item.description" slot-scope="{ item }">
          <div class="text-truncate" style="max-width: 200px;" :title="item.description">
            {{ item.description || $t('role.label.no_description') }}
          </div>
        </template>

        <!-- Guard Name Column -->
        <template slot="item.guard_name" slot-scope="{ item }">
          <v-chip
            :color="getGuardColor(item.guard_name)"
            small
            outlined
          >
            {{ item.guard_name }}
          </v-chip>
        </template>

        <!-- Users Count Column -->
        <template slot="item.users_count" slot-scope="{ item }">
          <v-chip
            color="info"
            small
            outlined
          >
            {{ item.users_count || 0 }} {{ $t('role.label.users') }}
          </v-chip>
        </template>

        <!-- Permissions Count Column -->
        <template slot="item.permissions_count" slot-scope="{ item }">
          <v-chip
            color="success"
            small
            outlined
          >
            {{ item.permissions_count || 0 }} {{ $t('permission.label.permissions') }}
          </v-chip>
        </template>

        <!-- Created At Column -->
        <template slot="item.created_at" slot-scope="{ item }">
          <div class="text-caption">
            {{ $shortDate(item.created_at) }}
          </div>
        </template>

        <!-- Updated At Column -->
        <template slot="item.updated_at" slot-scope="{ item }">
          <div class="text-caption">
            {{ $shortDate(item.updated_at) }}
          </div>
        </template>

        <!-- Actions Column -->
        <template slot="item.actions" slot-scope="{ item }">
          <div class="d-flex align-center">
            <v-tooltip bottom>
              <template slot="activator" slot-scope="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="assignPermissions(item)"
                  v-can="'assign permissions'"
                >
                  <v-icon small>mdi-key</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('role.button.assign_permission') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template slot="activator" slot-scope="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="info"
                  v-bind="attrs"
                  v-on="on"
                  @click="editRole(item)"
                  v-can="'edit roles'"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('role.button.edit_role') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template slot="activator" slot-scope="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="error"
                  v-bind="attrs"
                  v-on="on"
                  @click="deleteRole(item)"
                  v-can="'delete roles'"
                  :disabled="item.system"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('role.button.delete_role') }}</span>
            </v-tooltip>
          </div>
        </template>

        <!-- Empty State -->
        <template v-slot:no-data>
          <div class="text-center py-8">
            <v-icon color="grey lighten-1" size="64">mdi-shield-off</v-icon>
            <p class="text--secondary mt-2">{{ $t('role.dashboard.no_roles') }}</p>
            <v-btn
              color="primary"
              :to="{ name: 'create-role' }"
              v-can="'create roles'"
            >
              <v-icon left>mdi-plus</v-icon>
              {{ $t('role.button.create') }}
            </v-btn>
          </div>
        </template>

        <!-- Loading State -->
        <template v-slot:loading>
          <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modals -->
    <role-edit-modal
      v-if="role.id"
      :role="role"
      @updated="roleUpdated"
      @close="role = {}"
    />

    <role-assign-permission-modal
      v-if="assign.id"
      :role="assign"
      @close="assign = {}"
    />
  </v-container>
</template>

<script>
import RoleEditModal from './Modals/Edit'
import RoleAssignPermissionModal from './Modals/AssignPermissions'

export default {
  name: 'roles-index',
  components: {
    RoleEditModal,
    RoleAssignPermissionModal
  },
  data () {
    return {
      roles: [],
      search: '',
      role: {},
      assign: {},
      loading: false
    }
  },
  computed: {
    headers () {
      return [
        {
          text: this.$t('role.label.name'),
          value: 'name',
          sortable: true,
          width: '200px'
        },
        {
          text: this.$t('role.label.description'),
          value: 'description',
          sortable: false,
          width: '250px'
        },
        {
          text: this.$t('role.label.guard_name'),
          value: 'guard_name',
          sortable: true,
          width: '120px'
        },
        {
          text: this.$t('role.label.users'),
          value: 'users_count',
          sortable: true,
          width: '120px'
        },
        {
          text: this.$t('permission.label.permissions'),
          value: 'permissions_count',
          sortable: true,
          width: '140px'
        },
        {
          text: this.$t('label.created_at'),
          value: 'created_at',
          sortable: true,
          width: '120px'
        },
        {
          text: this.$t('label.updated_at'),
          value: 'updated_at',
          sortable: true,
          width: '120px'
        },
        {
          text: this.$t('label.actions'),
          value: 'actions',
          sortable: false,
          width: '120px',
          align: 'center'
        }
      ]
    },
    filteredRoles () {
      if (!this.search) return this.roles

      const searchLower = this.search.toLowerCase()
      return this.roles.filter(role =>
        role.name.toLowerCase().includes(searchLower) ||
        (role.description && role.description.toLowerCase().includes(searchLower)) ||
        role.guard_name.toLowerCase().includes(searchLower)
      )
    },
    activeRoles () {
      return this.roles.filter(role => !role.system).length
    },
    systemRoles () {
      return this.roles.filter(role => role.system).length
    },
    customRoles () {
      return this.roles.filter(role => !role.system).length
    }
  },
  methods: {
    editRole (item) {
      this.role = { ...item }
    },
    assignPermissions (item) {
      this.assign = { ...item }
    },
    deleteRole (item) {
      this.$confirm({
        title: this.$t('role.confirm.delete'),
        text: this.$t('role.confirm.delete_text', { name: item.name }),
        confirmButtonText: this.$t('button.delete'),
        cancelButtonText: this.$t('button.cancel'),
        type: 'warning'
      }).then(() => {
        this.loading = true
        this.$roleService.deleteRole(item.id)
          .then(() => {
            this.$message.success(this.$t('role.message.deleted'))
            this.loadRoles()
          })
          .catch(this.globalErrHandle)
          .finally(() => {
            this.loading = false
          })
      }).catch(() => {
        // User cancelled
      })
    },
    roleUpdated (data, message) {
      const index = this.roles.findIndex(role => role.id === data.id)
      if (index !== -1) {
        this.roles.splice(index, 1, data)
      }
      this.role = {}
      if (message) {
        this.$message.success(message)
      }
    },
    async loadRoles () {
      try {
        this.loading = true
        this.roles = await this.$roleService.getRoles()
      } catch (error) {
        console.log('Component caught error in loadRoles:', error)
      } finally {
        this.loading = false
      }
    },
    getRoleColor (roleName) {
      const colors = ['primary', 'success', 'warning', 'info', 'error', 'purple', 'pink', 'indigo']
      const index = roleName.toLowerCase().charCodeAt(0) % colors.length
      return colors[index]
    },
    getGuardColor (guard) {
      const colors = {
        api: 'primary',
        web: 'secondary',
        admin: 'success'
      }
      return colors[guard] || 'grey'
    }
  },
  mounted () {
    this.$emit('parent-title', this.$t('role.title.role'))
    this.loadRoles()
  }
}
</script>

<style scoped>
.search-field {
  max-width: 300px;
}

.v-data-table {
  border-radius: 0 0 12px 12px;
}

.v-data-table >>> .v-data-table__wrapper {
  border-radius: 0 0 12px 12px;
}

.v-card {
  border-radius: 12px;
}

.v-btn {
  border-radius: 8px;
}

.v-chip {
  border-radius: 6px;
}

.v-skeleton-loader {
  margin: 16px;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
